import { useErrorHandler } from 'react-error-boundary';
import {
  MutationFunction,
  QueryKey,
  useMutation as useMutationReactQuery,
  UseMutationOptions,
  UseMutationResult,
} from 'react-query';
import { ApiError, ForbiddenError, UnauthorizedError, ValidationError } from 'domains/api/errors';
import { useUserClientsWithActiveClient } from 'domains/auth/UserClientsWrapper/useUserClients';
import { FlashVariant, useFlashMessages } from 'domains/flash-messages';
import { queryClient } from './queryClient';

type WithNonFieldErrors<T> = T & {
  validationErrors: {
    nonFieldErrors: { message: string }[];
  };
};

type CustomUseMutationOptions<TError> = {
  onError?: (error: TError) => boolean | Promise<boolean> | void;
  invalidateQueries?: QueryKey[];
};

const hasNonFieldErrors = <T extends ApiError>(error: T): error is WithNonFieldErrors<T> => {
  if (error.validationErrors && 'nonFieldErrors' in error.validationErrors) {
    return true;
  }

  return false;
};

export const useMutation = <TData = unknown, TError extends ApiError = ApiError, TVariables = void, TContext = unknown>(
  mutationFn: MutationFunction<TData, TVariables>,
  options?: Omit<UseMutationOptions<TData, TError, TVariables, TContext>, 'mutationFn' | 'onError'> &
    CustomUseMutationOptions<TError>,
): UseMutationResult<TData, TError, TVariables, TContext> => {
  const { signOut } = useUserClientsWithActiveClient();
  const { showFlash } = useFlashMessages();
  const handleError = useErrorHandler();

  const { onSuccess, onError, invalidateQueries, ...opts } = { onError: () => false, ...options };

  return useMutationReactQuery(mutationFn, {
    ...opts,
    onSuccess: async (result, variables, context) => {
      if (invalidateQueries) {
        for (const query of invalidateQueries) {
          queryClient.invalidateQueries(query);
        }
      }

      if (onSuccess) {
        await onSuccess(result, variables, context);
      }
    },
    onError: async error => {
      /**
       * If the server responded with any errors that are not specific to a field
       * within the form, display them as flash error messages.
       */
      if (error instanceof ValidationError && hasNonFieldErrors(error)) {
        const errorMessages = error.validationErrors.nonFieldErrors.map(({ message }) => {
          return {
            variant: 'error' as FlashVariant,
            message,
          };
        });

        if (errorMessages) {
          showFlash(errorMessages);
        }
      }

      /**
       * If there are validation errors on the response, assume that the UI will
       * handle them.
       */
      const isHandled = error instanceof ValidationError || (await onError(error));

      if (!isHandled) {
        handleError(error);
      }

      if (error instanceof UnauthorizedError || error instanceof ForbiddenError) {
        signOut();
      }
    },
  });
};
